/* eslint-disable max-lines */
import {
  CarrierInfoDto,
  CommunicationDisplayConditionsDto,
  EnrollmentStatus,
} from '@zorro/clients';
import { APP_ROUTES, isDefined } from '@zorro/common-utils';
import { formatDateISO, parseDateISO } from '@zorro/shared/formatters';

import {
  BUTTON_LABELS,
  COMMUNICATION_ICONS,
  CommunicationFlagItem,
  CommunicationMessageConfig,
  FLAG_CONTENT,
  FlagType,
  SECTION_TITLES,
} from './CommunicationConfig';

export type PaymentCallbacks = {
  onMarkInitialPaymentDone: () => Promise<void>;
  onMarkAutoPayDone: (date: string | null) => Promise<void>;
};

const isElectionWindowStatus = (status: EnrollmentStatus): boolean => {
  return (
    status === EnrollmentStatus.PENDING_ELECTION_WINDOW ||
    status === EnrollmentStatus.ELECTION_ACTIVE ||
    status === EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED ||
    status === EnrollmentStatus.ELECTION_ACTIVE_STARTED
  );
};

type CreateAorFlagProps = {
  employeeName?: string;
  priorEnrollmentEffectiveUntil: string | null;
  onMarkAsDone?: () => Promise<void>;
};

const createAorFlag = ({
  employeeName,
  priorEnrollmentEffectiveUntil,
  onMarkAsDone,
}: CreateAorFlagProps): CommunicationFlagItem => {
  const aorContent = FLAG_CONTENT.AOR({
    employeeName,
    priorEnrollmentEffectiveUntil,
  });
  return {
    type: FlagType.AOR,
    icon: COMMUNICATION_ICONS.ALERT_CIRCLE,
    title: aorContent.title,
    body: aorContent.body,
    ...(onMarkAsDone && {
      button: {
        label: BUTTON_LABELS.MARK_AS_DONE,
        onClick: onMarkAsDone,
        variant: 'primary',
        leftSection: COMMUNICATION_ICONS.CHECK,
      },
    }),
  };
};

type CreateSelfEnrollFlagProps = {
  employeeName?: string;
  isSummaryPage?: boolean;
};

const createSelfEnrollFlag = ({
  employeeName,
  isSummaryPage,
}: CreateSelfEnrollFlagProps): CommunicationFlagItem => {
  const selfEnrollContent = FLAG_CONTENT.SELF_ENROLL({
    employeeName,
    isSummaryPage,
  });
  return {
    type: FlagType.SELF_ENROLL,
    icon: COMMUNICATION_ICONS.ALERT_CIRCLE,
    title: selfEnrollContent.title,
    body: selfEnrollContent.body,
  };
};

type CreateCarrierAppSentFlagProps = {
  specialRequestsCarrierAppSent: string;
  onMarkAsDone: () => Promise<void>;
  employeeName?: string;
};

const createCarrierAppSentFlag = ({
  specialRequestsCarrierAppSent,
  onMarkAsDone,
  employeeName,
}: CreateCarrierAppSentFlagProps): CommunicationFlagItem => {
  const carrierAppSentContent = FLAG_CONTENT.CARRIER_APP_SENT(employeeName);
  return {
    type: FlagType.CARRIER_APP_SENT,
    icon: COMMUNICATION_ICONS.ALERT_CIRCLE,
    title: carrierAppSentContent.title,
    body: employeeName
      ? `The carrier asked ${employeeName} to complete the following: \n${specialRequestsCarrierAppSent}`
      : specialRequestsCarrierAppSent,
    button: {
      label: BUTTON_LABELS.MARK_AS_DONE,
      onClick: onMarkAsDone,
      variant: 'primary',
      leftSection: COMMUNICATION_ICONS.CHECK,
    },
  };
};

type CreateInitialPaymentFlagProps = {
  employeeName?: string;
  onMarkAsDone: () => Promise<void>;
};

const createInitialPaymentFlag = ({
  onMarkAsDone,
  employeeName,
}: CreateInitialPaymentFlagProps): CommunicationFlagItem => {
  const initialPayment = FLAG_CONTENT.INITIAL_PAYMENT(employeeName);
  return {
    type: FlagType.INITIAL_PAYMENT,
    icon: COMMUNICATION_ICONS.CREDIT_CARD,
    title: initialPayment.title,
    body: initialPayment.body,
    link: {
      label: initialPayment.linkLabel,
      url: initialPayment.linkUrl,
    },
    button: {
      label: BUTTON_LABELS.MARK_AS_DONE,
      onClick: onMarkAsDone,
      variant: 'primary',
      leftSection: COMMUNICATION_ICONS.CHECK,
    },
    secondaryButton: {
      label: BUTTON_LABELS.VIEW_ZORRO_PAY,
      variant: 'secondary',
      leftSection: COMMUNICATION_ICONS.CREDIT_CARD_ICON,
    },
  };
};

type CreateAutoPayFlagProps = {
  onMarkAsDone: (date: string | null) => Promise<void>;
  employeeName?: string;
};

const createAutoPayFlag = ({
  onMarkAsDone,
  employeeName,
}: CreateAutoPayFlagProps): CommunicationFlagItem => {
  const autoPayConfig = FLAG_CONTENT.AUTO_PAY(employeeName);
  return {
    type: FlagType.AUTO_PAY,
    icon: COMMUNICATION_ICONS.CREDIT_CARD,
    title: autoPayConfig.title,
    body: autoPayConfig.body,
    link: {
      label: autoPayConfig.linkLabel,
      url: autoPayConfig.linkUrl,
    },
    button: {
      label: BUTTON_LABELS.MARK_AS_DONE,
      onClick: (date) => onMarkAsDone(date ?? null),
      variant: 'primary',
      leftSection: COMMUNICATION_ICONS.CHECK,
    },
    secondaryButton: {
      label: BUTTON_LABELS.VIEW_ZORRO_PAY,
      variant: 'secondary',
      leftSection: COMMUNICATION_ICONS.CREDIT_CARD_ICON,
    },
  };
};

type CreateAutoPayEnrollmentConfirmedFlagProps = {
  onMarkAsDone: (date: string | null) => Promise<void>;
  employeeName?: string;
};

const createAutoPayEnrollmentConfirmedFlag = ({
  onMarkAsDone,
  employeeName,
}: CreateAutoPayEnrollmentConfirmedFlagProps): CommunicationFlagItem => {
  const autoPayEnrollmentConfirmedConfig =
    FLAG_CONTENT.AUTO_PAY_ENROLLMENT_CONFIRMED(employeeName);
  return {
    type: FlagType.AUTO_PAY_ENROLLMENT_CONFIRMED,
    icon: COMMUNICATION_ICONS.ALERT_CIRCLE,
    title: autoPayEnrollmentConfirmedConfig.title,
    body: autoPayEnrollmentConfirmedConfig.body,
    link: {
      label: autoPayEnrollmentConfirmedConfig.linkLabel,
      url: autoPayEnrollmentConfirmedConfig.linkUrl,
    },
    button: {
      label: BUTTON_LABELS.MARK_AS_DONE,
      onClick: (date) => onMarkAsDone(date ?? null),
      variant: 'primary',
      leftSection: COMMUNICATION_ICONS.CHECK,
    },
    secondaryButton: {
      label: BUTTON_LABELS.VIEW_ZORRO_PAY,
      variant: 'secondary',
      leftSection: COMMUNICATION_ICONS.CREDIT_CARD_ICON,
    },
  };
};

type CreateNextStepsFlagProps = {
  employeeName?: string;
  conditions: CommunicationDisplayConditionsDto;
  specialRequestsElectionSubmitted?: string | null;
  filesPageUrl?: string;
};

const createNextStepsFlag = ({
  conditions,
  employeeName,
  specialRequestsElectionSubmitted,
  filesPageUrl,
}: CreateNextStepsFlagProps): CommunicationFlagItem | null => {
  const steps: string[] = [];
  const nextStepConfig = FLAG_CONTENT.NEXT_STEPS(employeeName);

  if (conditions.isInitialPayments) {
    steps.push(nextStepConfig.initialPayments);
  }

  if (conditions.isAutoPay) {
    steps.push(nextStepConfig.autoPay);
  }

  if (
    conditions.isCarrierApplicationElectionSubmitted &&
    specialRequestsElectionSubmitted
  ) {
    steps.push(specialRequestsElectionSubmitted);
  }

  if (steps.length === 0 && !filesPageUrl) {
    return null;
  }

  return {
    type: FlagType.NEXT_STEPS,
    icon: COMMUNICATION_ICONS.INFO_CIRCLE,
    title: nextStepConfig.title,
    body: steps,
    filesPageUrl,
  };
};

type BuildActivePhaseCommunicationProps = {
  conditions: CommunicationDisplayConditionsDto;
  priorEnrollmentEffectiveUntil: string | null;
  onMarkAorCancellationDone?: () => Promise<void>;
  employeeName?: string;
};

const buildActivePhaseCommunication = ({
  conditions,
  priorEnrollmentEffectiveUntil,
  onMarkAorCancellationDone,
  employeeName,
}: BuildActivePhaseCommunicationProps): CommunicationMessageConfig | null => {
  if (!conditions.isAor) {
    return null;
  }

  return {
    sectionTitle: SECTION_TITLES.ACTION_NEEDED,
    flags: [
      createAorFlag({
        priorEnrollmentEffectiveUntil,
        onMarkAsDone: onMarkAorCancellationDone,
        employeeName,
      }),
    ],
  };
};

type BuildElectionSubmittedCommunicationProps = {
  conditions: CommunicationDisplayConditionsDto;
  priorEnrollmentEffectiveUntil: string | null;
  carrierInfo?: CarrierInfoDto;
  onMarkAorCancellationDone?: () => Promise<void>;
  isSummaryPage?: boolean;
  employeeName?: string;
  filesPageUrl?: string;
};

const buildElectionSubmittedCommunication = ({
  conditions,
  priorEnrollmentEffectiveUntil,
  carrierInfo,
  onMarkAorCancellationDone,
  isSummaryPage,
  employeeName,
  filesPageUrl,
}: BuildElectionSubmittedCommunicationProps): CommunicationMessageConfig | null => {
  const flags: CommunicationFlagItem[] = [];

  if (conditions.isSelfEnrollAndStatusElectionSubmitted) {
    flags.push(createSelfEnrollFlag({ employeeName, isSummaryPage }));
  }

  if (conditions.isAor) {
    flags.push(
      createAorFlag({
        priorEnrollmentEffectiveUntil,
        onMarkAsDone: onMarkAorCancellationDone,
        employeeName,
      })
    );
  }

  const nextStepsFlag = createNextStepsFlag({
    conditions,
    employeeName,
    specialRequestsElectionSubmitted:
      carrierInfo?.specialRequestsElectionSubmitted,
    filesPageUrl,
  });
  if (nextStepsFlag) {
    flags.push(nextStepsFlag);
  }

  if (flags.length === 0) {
    return null;
  }

  const sectionTitle =
    conditions.isSelfEnrollAndStatusElectionSubmitted || conditions.isAor
      ? SECTION_TITLES.ACTION_TO_FINALIZE
      : undefined;

  return {
    sectionTitle,
    flags,
  };
};

type BuildCarrierApplicationSentCommunicationProps = {
  conditions: CommunicationDisplayConditionsDto;
  priorEnrollmentEffectiveUntil: string | null;
  paymentCallbacks: PaymentCallbacks;
  onMarkCarrierRequirementsDone: () => Promise<void>;
  carrierInfo?: CarrierInfoDto;
  onMarkAorCancellationDone?: () => Promise<void>;
  employeeName?: string;
};

const buildCarrierApplicationSentCommunication = ({
  conditions,
  priorEnrollmentEffectiveUntil,
  paymentCallbacks,
  onMarkCarrierRequirementsDone,
  carrierInfo,
  onMarkAorCancellationDone,
  employeeName,
}: BuildCarrierApplicationSentCommunicationProps): CommunicationMessageConfig | null => {
  const flags: CommunicationFlagItem[] = [];

  if (conditions.isAor) {
    flags.push(
      createAorFlag({
        priorEnrollmentEffectiveUntil,
        onMarkAsDone: onMarkAorCancellationDone,
        employeeName,
      })
    );
  }

  if (
    conditions.isCarrierApplicationRequestSent &&
    isDefined(carrierInfo?.specialRequestsCarrierAppSent)
  ) {
    flags.push(
      createCarrierAppSentFlag({
        specialRequestsCarrierAppSent:
          carrierInfo.specialRequestsCarrierAppSent,
        onMarkAsDone: onMarkCarrierRequirementsDone,
        employeeName,
      })
    );
  }

  if (conditions.isInitialPayments) {
    flags.push(
      createInitialPaymentFlag({
        onMarkAsDone: paymentCallbacks.onMarkInitialPaymentDone,
        employeeName,
      })
    );
  }

  if (conditions.isAutoPay) {
    flags.push(
      createAutoPayFlag({
        onMarkAsDone: paymentCallbacks.onMarkAutoPayDone,
        employeeName,
      })
    );
  }

  if (flags.length === 0) {
    return null;
  }

  return {
    sectionTitle: SECTION_TITLES.ACTION_TO_FINALIZE,
    flags,
  };
};

type BuildEnrollmentConfirmedOrActiveCommunicationProps = {
  conditions: CommunicationDisplayConditionsDto;
  paymentCallbacks: PaymentCallbacks;
  priorEnrollmentEffectiveUntil: string | null;
  onMarkAorCancellationDone?: () => Promise<void>;
  employeeName?: string;
};

const buildEnrollmentConfirmedOrActiveCommunication = ({
  conditions,
  paymentCallbacks,
  priorEnrollmentEffectiveUntil,
  onMarkAorCancellationDone,
  employeeName,
}: BuildEnrollmentConfirmedOrActiveCommunicationProps): CommunicationMessageConfig | null => {
  const flags: CommunicationFlagItem[] = [];

  if (conditions.isAor) {
    flags.push(
      createAorFlag({
        priorEnrollmentEffectiveUntil,
        onMarkAsDone: onMarkAorCancellationDone,
        employeeName,
      })
    );
  }

  if (conditions.isInitialPayments) {
    flags.push(
      createInitialPaymentFlag({
        onMarkAsDone: paymentCallbacks.onMarkInitialPaymentDone,
        employeeName,
      })
    );
  }

  if (conditions.isAutoPay) {
    flags.push(
      createAutoPayEnrollmentConfirmedFlag({
        onMarkAsDone: paymentCallbacks.onMarkAutoPayDone,
        employeeName,
      })
    );
  }

  if (flags.length === 0) {
    return null;
  }

  return {
    sectionTitle: SECTION_TITLES.ACTION_TO_FINALIZE,
    flags,
  };
};

type EmployeeModeProps =
  | { isEmployeeMode: true; employeeFirstName?: string }
  | { isEmployeeMode: false; employeeFirstName: string };

type BuildCommunicationMessageProps = EmployeeModeProps & {
  status: EnrollmentStatus;
  effectiveFrom: string | null;
  communicationConditions?: CommunicationDisplayConditionsDto;
  carrierInfo?: CarrierInfoDto;
  isSummaryPage?: boolean;
  paymentCallbacks?: PaymentCallbacks;
  onMarkAorCancellationDone?: () => Promise<void>;
  onMarkCarrierRequirementsDone?: () => Promise<void>;
  onboardingPeriodId?: string;
};

export function buildCommunicationMessage({
  status,
  employeeFirstName,
  effectiveFrom,
  communicationConditions,
  carrierInfo,
  isSummaryPage,
  isEmployeeMode,
  paymentCallbacks,
  onMarkAorCancellationDone,
  onMarkCarrierRequirementsDone,
  onboardingPeriodId,
}: BuildCommunicationMessageProps): CommunicationMessageConfig | undefined {
  if (!communicationConditions) {
    return undefined;
  }
  const priorEnrollmentEffectiveUntil = effectiveFrom
    ? formatDateISO(parseDateISO(effectiveFrom).subtract(1, 'day'))
    : null;
  const employeeName = isEmployeeMode ? undefined : employeeFirstName;

  if (isElectionWindowStatus(status)) {
    return (
      buildActivePhaseCommunication({
        conditions: communicationConditions,
        priorEnrollmentEffectiveUntil,
        onMarkAorCancellationDone,
        employeeName,
      }) ?? undefined
    );
  }

  if (status === EnrollmentStatus.ELECTION_SUBMITTED) {
    return (
      buildElectionSubmittedCommunication({
        conditions: communicationConditions,
        priorEnrollmentEffectiveUntil,
        carrierInfo,
        onMarkAorCancellationDone,
        isSummaryPage,
        employeeName,
        filesPageUrl:
          isEmployeeMode &&
          onboardingPeriodId &&
          communicationConditions.isDocumentsRequired
            ? APP_ROUTES.FILES({ onboardingPeriodId })
            : undefined,
      }) ?? undefined
    );
  }

  if (
    status === EnrollmentStatus.ENROLLMENT_IN_PROCESS &&
    paymentCallbacks &&
    onMarkCarrierRequirementsDone
  ) {
    return (
      buildCarrierApplicationSentCommunication({
        conditions: communicationConditions,
        priorEnrollmentEffectiveUntil,
        paymentCallbacks,
        onMarkCarrierRequirementsDone,
        carrierInfo,
        onMarkAorCancellationDone,
        employeeName,
      }) ?? undefined
    );
  }

  if (
    (status === EnrollmentStatus.ENROLLMENT_CONFIRMED ||
      status === EnrollmentStatus.ACTIVE_COVERAGE) &&
    paymentCallbacks
  ) {
    return (
      buildEnrollmentConfirmedOrActiveCommunication({
        conditions: communicationConditions,
        paymentCallbacks,
        priorEnrollmentEffectiveUntil,
        onMarkAorCancellationDone,
        employeeName,
      }) ?? undefined
    );
  }

  return undefined;
}
